import React, { useEffect, useMemo, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import BulkCsvDialog from "@/components/BulkCsvDialog";

const EMPTY = { full_name: "", jersey_number: 1, team_id: "", tournament_ids: [], photo_url: "", position: "" };

export default function AdminPlayers() {
  const [items, setItems] = useState([]);
  const [teams, setTeams] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [filterTeam, setFilterTeam] = useState("all");
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = async () => {
    const [p, t, tt] = await Promise.all([api.get("/players"), api.get("/teams"), api.get("/tournaments")]);
    setItems(p.data); setTeams(t.data); setTournaments(tt.data);
  };
  useEffect(() => { load(); }, []);

  const teamById = useMemo(() => Object.fromEntries(teams.map((t) => [t.id, t])), [teams]);
  const tourById = useMemo(() => Object.fromEntries(tournaments.map((t) => [t.id, t])), [tournaments]);
  const teamTournamentIds = (team) => (team?.tournament_ids && team.tournament_ids.length ? team.tournament_ids : (team?.tournament_id ? [team.tournament_id] : []));

  const startCreate = () => {
    setEditing(null);
    const defaultTeam = filterTeam !== "all" ? filterTeam : (teams[0]?.id || "");
    const t = teamById[defaultTeam];
    setForm({ ...EMPTY, team_id: defaultTeam, tournament_ids: teamTournamentIds(t) });
    setOpen(true);
  };
  const startEdit = (p) => {
    setEditing(p);
    const t = teamById[p.team_id];
    const tids = (p.tournament_ids && p.tournament_ids.length) ? p.tournament_ids : teamTournamentIds(t);
    setForm({ ...EMPTY, ...p, tournament_ids: tids });
    setOpen(true);
  };

  const onTeamChange = (newTeamId) => {
    const t = teamById[newTeamId];
    setForm((f) => ({ ...f, team_id: newTeamId, tournament_ids: teamTournamentIds(t) }));
  };

  const togglePlayerTournament = (id) => {
    setForm((f) => {
      const has = (f.tournament_ids || []).includes(id);
      return { ...f, tournament_ids: has ? f.tournament_ids.filter((x) => x !== id) : [...(f.tournament_ids || []), id] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.team_id) return toast.error("Please select a team");
    if (!form.tournament_ids || form.tournament_ids.length === 0) return toast.error("Selecione ao menos um torneio");
    try {
      const payload = { ...form, jersey_number: Number(form.jersey_number) };
      if (editing) await api.put(`/players/${editing.id}`, payload);
      else await api.post("/players", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete player "${p.full_name}"?`)) return;
    await api.delete(`/players/${p.id}`); toast.success("Deleted"); load();
  };

  const filtered = useMemo(() => filterTeam === "all" ? items : items.filter((x) => x.team_id === filterTeam), [items, filterTeam]);
  const tnameMap = Object.fromEntries(teams.map((t) => [t.id, t]));

  return (
    <div className="p-6 md:p-10">
      <PageHeader
        title="Players"
        subtitle="Cadastre jogadores. Máx. 10 por time."
        testid="page-players-admin"
        action={
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="bulk-import-players-btn"
              onClick={() => setBulkOpen(true)}
              disabled={teams.length === 0}
              variant="outline"
              className="bg-transparent border-navy-border text-white hover:bg-navy-surface hover:text-orange font-heading uppercase tracking-widest rounded-sm"
            >
              <Upload size={16} className="mr-2" /> Importar CSV
            </Button>
            <Button data-testid="create-player-btn" onClick={startCreate} disabled={teams.length === 0} className="bg-orange hover:bg-orange-hover text-white font-heading uppercase tracking-widest rounded-sm">
              <Plus size={16} className="mr-2" /> New player
            </Button>
          </div>
        }
      />

      {teams.length === 0 && <div className="border border-orange/40 bg-orange/5 text-orange p-4 rounded-sm mb-4 text-sm">Create a team first.</div>}

      <div className="mb-4 max-w-xs">
        <Select value={filterTeam} onValueChange={setFilterTeam}>
          <SelectTrigger data-testid="players-team-filter" className="bg-navy-deep border-navy-border text-white rounded-sm"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-navy-deep border-navy-border text-white">
            <SelectItem value="all">All teams</SelectItem>
            {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="border border-navy-border rounded-sm overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2 bg-navy-surface text-[10px] font-heading uppercase tracking-widest text-ink-muted">
          <div className="col-span-1">#</div>
          <div className="col-span-1">Photo</div>
          <div className="col-span-4">Name</div>
          <div className="col-span-4">Team</div>
          <div className="col-span-1">Pos</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {filtered.length === 0 ? <div className="p-8 text-center text-ink-muted">No players</div> :
          filtered.map((p) => (
            <div key={p.id} className="grid grid-cols-12 items-center px-4 py-3 border-t border-navy-border" data-testid={`player-row-${p.id}`}>
              <div className="col-span-1 font-mono tabular font-black text-orange">{p.jersey_number}</div>
              <div className="col-span-1">
                <div className="w-10 h-10 rounded-sm bg-navy-surface border border-navy-border grid place-items-center overflow-hidden">
                  {p.photo_url ? <img src={fileUrl(p.photo_url)} alt="" className="w-full h-full object-cover" />
                    : <span className="font-heading font-bold text-white text-sm">{p.full_name?.[0]}</span>}
                </div>
              </div>
              <div className="col-span-4 font-heading uppercase tracking-tight text-white">{p.full_name}</div>
              <div className="col-span-4 text-ink-secondary text-sm">{tnameMap[p.team_id]?.name || "—"}</div>
              <div className="col-span-1 text-ink-secondary text-sm">{p.position || "—"}</div>
              <div className="col-span-1 flex justify-end gap-1">
                <Button data-testid={`edit-player-${p.id}`} size="icon" variant="ghost" onClick={() => startEdit(p)} className="text-ink-secondary hover:text-orange hover:bg-navy-surface"><Pencil size={14} /></Button>
                <Button data-testid={`delete-player-${p.id}`} size="icon" variant="ghost" onClick={() => remove(p)} className="text-ink-secondary hover:text-court-red hover:bg-navy-surface"><Trash2 size={14} /></Button>
              </div>
            </div>
          ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-navy-deep border-navy-border text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight text-2xl">{editing ? "Edit player" : "New player"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="player-form">
            <div>
              <Label className="text-ink-secondary uppercase tracking-widest text-xs">Full name</Label>
              <Input data-testid="player-name-input" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="bg-navy border-navy-border mt-2 rounded-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-ink-secondary uppercase tracking-widest text-xs">Jersey #</Label>
                <Input data-testid="player-jersey-input" type="number" min={0} required value={form.jersey_number} onChange={(e) => setForm({ ...form, jersey_number: e.target.value })} className="bg-navy border-navy-border mt-2 rounded-sm font-mono" />
              </div>
              <div>
                <Label className="text-ink-secondary uppercase tracking-widest text-xs">Position</Label>
                <Input value={form.position || ""} onChange={(e) => setForm({ ...form, position: e.target.value })} placeholder="G / F / C" className="bg-navy border-navy-border mt-2 rounded-sm" />
              </div>
            </div>
            <div>
              <Label className="text-ink-secondary uppercase tracking-widest text-xs">Team</Label>
              <Select value={form.team_id} onValueChange={onTeamChange}>
                <SelectTrigger data-testid="player-team-select" className="bg-navy border-navy-border text-white mt-2 rounded-sm"><SelectValue placeholder="Select team…" /></SelectTrigger>
                <SelectContent className="bg-navy-deep border-navy-border text-white">
                  {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {(() => {
              const teamTids = teamTournamentIds(teamById[form.team_id]);
              if (teamTids.length <= 1) return null;
              return (
                <div>
                  <Label className="text-ink-secondary uppercase tracking-widest text-xs">Torneios do atleta (subconjunto dos torneios do time)</Label>
                  <div data-testid="player-tournaments-multi" className="mt-2 border border-navy-border rounded-sm bg-navy p-2 max-h-40 overflow-y-auto space-y-1">
                    {teamTids.map((tid) => {
                      const checked = (form.tournament_ids || []).includes(tid);
                      return (
                        <label
                          key={tid}
                          data-testid={`player-tournament-toggle-${tid}`}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded-sm cursor-pointer transition-colors ${checked ? "bg-orange/10 border border-orange/40" : "border border-transparent hover:bg-navy-surface"}`}
                        >
                          <input type="checkbox" checked={checked} onChange={() => togglePlayerTournament(tid)} className="accent-orange" />
                          <span className="text-sm text-white font-heading uppercase tracking-tight">{tourById[tid]?.name || tid}</span>
                        </label>
                      );
                    })}
                  </div>
                  <div className="mt-2 text-[10px] text-ink-muted font-mono uppercase tracking-widest">
                    Somente jogos em torneios selecionados aparecerão no elenco do operador para este atleta.
                  </div>
                </div>
              );
            })()}
            <ImageUpload label="Photo" value={form.photo_url} onChange={(url) => setForm({ ...form, photo_url: url })} testid="player-photo" />
            <DialogFooter>
              <Button data-testid="player-submit-btn" type="submit" className="bg-orange hover:bg-orange-hover text-white font-heading uppercase tracking-widest rounded-sm">{editing ? "Save" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <BulkCsvDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        endpoint="/players/bulk-csv"
        title="Importar jogadores (CSV)"
        templateHeader="full_name,jersey_number,team,tournaments,position"
        templateExample={`Michael Jordan,23,${teams[0]?.name || "Team Thunder"},,G\nLarry Bird,33,${teams[0]?.name || "Team Thunder"},,F`}
        templateFilename="players_template.csv"
        columnHelp={[
          { name: "full_name", desc: "(obrigatório) Nome completo" },
          { name: "jersey_number", desc: "(obrigatório) Número inteiro da camisa" },
          { name: "team ou team_id", desc: "(obrigatório) Nome exato OU ID do time" },
          { name: "tournaments", desc: "(opcional) Torneios em que o atleta participa (nomes ou IDs, separados por ; ou |). Se vazio, herda todos os torneios do time." },
          { name: "position", desc: "(opcional) Posição (G / F / C)" },
        ]}
        onDone={load}
      />
    </div>
  );
}
